import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useVersionWatcher } from '@/lib/useVersionWatcher'
import {
  getServiceWorkerActive,
  onServiceWorkerActivation,
  onUpdateAvailable,
  reloadForUpdate,
} from '@/lib/pwa/updateSignal'

// stable id so repeat triggers collapse into the single standing prompt rather
// than stacking duplicate toasts.
const UPDATE_TOAST_ID = 'app-version-update'

const promptForUpdate = () => {
  toast('A new version is available', {
    id: UPDATE_TOAST_ID,
    description: 'Reload to get the latest updates.',
    duration: Infinity,
    action: {
      label: 'Reload',
      onClick: () => reloadForUpdate(),
    },
  })
}

// surfaces a dismissible "new version available" prompt when a newer frontend has
// been deployed. this is the non-intrusive half of the hybrid reload strategy —
// a plain version mismatch only offers a reload (the user clicks when ready);
// only a broken stale chunk hard-reloads (see lib/reloadOnPreloadError.ts).
//
// the signal comes from the service worker registered in main.tsx: a waiting worker means
// the new build is already downloaded, so accepting the prompt activates it rather than
// racing a fetch. polling /version.json remains the fallback wherever no worker registers
// (an insecure origin, or a browser with workers disabled), and the two never run at once.
// renders nothing; it exists purely to run the watcher within the app tree.
export const VersionWatcher = () => {
  const [serviceWorkerActive, setServiceWorkerActive] = useState(getServiceWorkerActive)

  useEffect(() => onUpdateAvailable(promptForUpdate), [])
  useEffect(
    () => onServiceWorkerActivation(() => setServiceWorkerActive(getServiceWorkerActive())),
    []
  )

  // stays inert while registration is still resolving (null), so a slow register cannot
  // let both the worker and the poller raise a prompt for the same deploy
  useVersionWatcher(promptForUpdate, { enabled: serviceWorkerActive === false })

  return null
}
